'use client'
import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import { useRef, useState } from 'react'
import { Tag } from '@/components/elementary/Tag'
import PricingListContent from '@/components/page-builder/global/pricing/PricingListContent'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { isRefSticky } from '@/hooks/useSticky'
import { cn } from '@/lib/styles'
import { PRICING_TEST_IDS } from '@/lib/test-ids'

export const PricingList = ({
	cardsData,
	pricingBanner,
}: {
	cardsData?: Data.Component<'shared.pricing-item'>[] | null
	pricingBanner?: Data.Component<'shared.pricing-banner'> | null
}) => {
	const t = useTranslations('pricing')

	const [yearly, setYearly] = useState(true)
	const [currentTab, setCurrentTab] = useState(cardsData?.[0]?.label ?? '')

	const stickyRef = useRef<HTMLDivElement>(null)
	const isStuck = isRefSticky(stickyRef, 64)
	return (
		<div data-test-id={PRICING_TEST_IDS.LIST}>
			<div
				className="flex justify-center lg:justify-start items-center "
				data-test-id={PRICING_TEST_IDS.YEARLY_SWITCH}
			>
				<Switch
					onCheckedChange={() => setYearly((value) => !value)}
					checked={yearly}
					labelLeft={t('billedMonthly')}
					labelRight={t('billedYearly')}
				/>
			</div>
			<div className="w-full py-3 lg:hidden">
				<Tabs
					value={currentTab}
					onValueChange={setCurrentTab}
					data-test-id={PRICING_TEST_IDS.CONTENT_TAB}
				>
					<TabsList
						className={cn(
							'flex justify-center w-auto px-0 sticky z-20 top-16 pt-3 pb-0 h-auto rounded-none bg-neutral-light-100 -mx-6 ',
							isStuck && 'border-b border-brand-black',
						)}
						ref={stickyRef}
					>
						<div className="flex overflow-auto px-6 pb-3 w-auto ">
							{cardsData?.map((item) => (
								<TabsTrigger
									value={String(item?.label)}
									key={`${item?.id}-tab`}
									data-test-id={PRICING_TEST_IDS.CONTENT_TAB_TRIGGER}
								>
									{item?.label}
									{item?.isPopularTagVisible && (
										<Tag
											name={t('mostPopular')}
											size="small"
											className="w-fit text-sm lg:text-sm ml-2 -mr-2"
											fontWeight="font-bold"
											active
											customColors={{
												backgroundColor: 'neutral-light-100',
												borderColor: 'brand-black',
											}}
										/>
									)}
								</TabsTrigger>
							))}
						</div>
					</TabsList>
					{cardsData?.map((item) => (
						<TabsContent
							className="w-full"
							value={String(item?.label)}
							key={`${item?.id}-content`}
							data-test-id={PRICING_TEST_IDS.CONTENT_TAB_CONTENT}
						>
							<PricingListContent
								cardsData={cardsData}
								pricingBanner={pricingBanner}
								currentTab={item?.label}
								yearly={yearly}
							/>
						</TabsContent>
					))}
				</Tabs>
			</div>

			<PricingListContent
				className="hidden lg:block"
				cardsData={cardsData}
				pricingBanner={pricingBanner}
				yearly={yearly}
			/>
		</div>
	)
}

export default PricingList
